import React from "react";
import DynamicTable from "../widgets/dynamicTable/DynamicTable";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreState } from "easy-peasy";
import HelperMetiers360 from "../../services/HelpersMetiers360.js";
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip, Image, Button } from 'react-bootstrap';
import { showDuration } from "./ActivityDetailsComponent.jsx";
import AuthorizationChecker from "../../services/AuthorizationChecker";

const ActivityTableComponent = (props) => {
    const { activities, createBlacklistModal } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const activitiesSlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('activity'));

    const handleClick = (uniqueId) => {
        navigate(activitiesSlugs.readOne.replace(':uniqueId', uniqueId), { state: { olderSearchParams: location.search } });
    }

    const { apiData: { BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const { activityStatus, activityAudienceTypes } = BACKOFFICE_DEFAULT_VALUES;

    const statusColors = { draft: 'danger', preprod: 'warning', lab: 'secondary', prod: 'success' };

    const iconifyStatus = (activity) => <div className="statusdiv">
        <i className={`fas fa-solid fa-circle fa-xs text-${statusColors[activity.status]}`} />{activityStatus?.find(s => s.value == activity.status)?.label}
    </div>;

    const handleBlacklist = (activity) => <div className="statusdiv">
        {activity.isBlackListed ? "Non affichée" : "Affichée"}
        <Button variant={activity?.isBlackListed ? 'success' : 'danger'} className='ml-4 p-2' size='sm' onClick={(e) => {
            e.stopPropagation();
            createBlacklistModal(activity);
        }}>
            {activity?.isBlackListed ? 'Afficher' : 'Retirer'}
        </Button>
    </div>;

    const displayAudienceTypes = (activity) => activity?.audienceType?.length > 0 ? activity?.audienceType?.map(audienceT => activityAudienceTypes?.find(at => at.value == audienceT)?.label)?.join(", ") : "Tout le monde";

    const sortType = activities?.length > 0
        && [
            {
                value: 'thumbnail', label: 'Vignette',
                test: HelperMetiers360.isArrayContainsValue(activities, 'thumbnail'),
                display: (activity) => (
                    <OverlayTrigger
                        placement='right'
                        overlay={
                            <Tooltip>
                                <Image src={activity.thumbnail} />
                            </Tooltip>
                        }
                    >
                        <Image src={activity.thumbnail} className="img-overlay" thumbnail />
                    </OverlayTrigger>
                ),
                flatDisplay: (activity) => activity.thumbnail
            },
            {
                value: 'title', label: 'Titre',
                test: HelperMetiers360.isArrayContainsValue(activities, "title"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.title, b.title),
                display: (activity) => activity.title ?? '-'
            },
            {
                value: 'duration', label: 'Durée',
                test: activities?.some(m => m.duration),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.duration, b.duration, true),
                display: (activity) => <span dangerouslySetInnerHTML={{ __html: showDuration(activity) }} />,
                flatDisplay: (activity) => activity.duration
            },
            {
                value: 'audienceType', label: 'Publics cibles',
                test: activities?.some(m => m.audienceType),
                method: (a, b) => HelperMetiers360.sortStringArray(a.audienceType, b.audienceType),
                display: (activity) => displayAudienceTypes(activity)
            },
        {
            value: 'endingCount', label: 'Réalisations',
            test: activities?.some(s => s.endingCount),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.endingCount, b.endingCount),
            display: (stat) => stat.endingCount
        },
        {
            value: 'userCount', label: 'Utilisateur⋅rices ayant réalisé',
            test: activities?.some(s => s.userCount),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.userCount, b.userCount),
            display: (stat) => stat.userCount
        },
        {
            value: 'currentCount', label: 'Utilisateur⋅rices ayant commencé',
            test: activities?.some(s => s.currentCount),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.currentCount, b.currentCount),
            display: (stat) => stat.currentCount
        },
        {
            value: 'publishedAt', label: 'Date de publication',
            test: HelperMetiers360.isArrayContainsValue(activities, "publishedAt"),
                method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.publishedAt, b.publishedAt),
                display: (activity) => activity.publishedAt ? HelperMetiers360.getdisplayDateType(activity.publishedAt, 'day') : (activity.status == 'lab' && !AuthorizationChecker.hasUpdateRights('activity')) ? "En test Lab" : ""
        },
        {
            value: 'clientsListData', label: 'Liste de clients restreinte',
            test: HelperMetiers360.isArrayContainsValue(activities, "clientsListData"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.clientsListData?.length, b.clientsListData?.length),
                display: (activity) => activity.clientsListData?.length > 0 ? "Oui" : "Non"
        },
        {
                value: 'createdAt', label: "Date de création",
                test: HelperMetiers360.isArrayContainsValue(activities, "createdAt"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.createdAt, b.createdAt),
                display: (activity) => HelperMetiers360.getdisplayDateType(activity.createdAt, 'day')
            },
            {
                value: 'status', label: 'Statut',
                test: AuthorizationChecker.hasUpdateRights('activity') && HelperMetiers360.isArrayContainsValue(activities, "status"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.status, b.status),
                display: (activity) => iconifyStatus(activity),
                flatDisplay: (activity) => activity.status
        },
        {
            value: 'isBlacklisted', label: 'Accès utilisateur·rices',
            test: HelperMetiers360.isArrayContainsValue(activities, "isBlackListed"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isBlackListed, b.isBlackListed, true),
            display: (activity) => handleBlacklist(activity),
            flatDisplay: (activity) => activity.isBlackListed ? "Non" : "Oui"
            }
        ];

    return sortType
        &&
        <DynamicTable
            contentTable={activities}
            contentSort={sortType}
        valueInitSort="publishedAt"
            index='uniqueId'
            handleClick={handleClick}
        withParams={true}
            tableName={"activitytable"} />;
};

ActivityTableComponent.propTypes = {
    activities: PropTypes.array.isRequired,
    createBlacklistModal: PropTypes.func,
};

export default ActivityTableComponent;